// Settings panel — operator-facing inbox of pending agent-tool runs.
//
// Surfaces every row in agent_tool_runs where status='pending_approval'
// for the signed-in owner. Each row shows what the AI wants to do +
// the cost/blast radius, and lets the operator Approve or Reject.
//
// Approve  → status='approved'   (agent's next call with the token executes)
// Reject   → status='rejected'   (agent gets 410 with reason)
//
// Realtime-subscribed so new pending rows appear without a refresh.

import React from 'react';
import { Bot, ShieldCheck, X, RefreshCcw, AlertTriangle, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';

const TOOL_LABEL = {
  'a2p-status':       'AI Agent · Check A2P status',
  'a2p-submit':       'AI Agent · Submit A2P brand registration',
  'provision-number': 'AI Agent · Provision new Twilio number',
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
};

export default function AgentToolApprovalsPanel({ currentUser, showToast = () => {} }) {
  const [runs, setRuns]       = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId]   = React.useState(null);
  const [showAll, setShowAll] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('agent_tool_runs')
      .select('id, tool, status, input, preview, result, approval_token, expires_at, created_at, executed_at, rejected_at, rejection_reason')
      .order('created_at', { ascending: false })
      .limit(showAll ? 50 : 20);
    if (error) showToast(`Load failed: ${error.message}`);
    setRuns(data || []);
    setLoading(false);
  }, [currentUser?.id, showAll, showToast]);

  React.useEffect(() => { load(); }, [load]);

  // Live updates: new pending rows appear when the agent fires a call.
  React.useEffect(() => {
    if (!currentUser?.id) return;
    const ch = supabase.channel(`agent_tool_runs_${currentUser.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'agent_tool_runs', filter: `owner_id=eq.${currentUser.id}` }, () => load())
      .subscribe();
    return () => { try { supabase.removeChannel(ch); } catch {} };
  }, [currentUser?.id, load]);

  const approve = async (run) => {
    setBusyId(run.id);
    const { error } = await supabase.from('agent_tool_runs')
      .update({ status: 'approved', approved_at: new Date().toISOString(), approved_by: currentUser.id })
      .eq('id', run.id).eq('status', 'pending_approval');
    if (error) showToast(`Approve failed: ${error.message}`);
    else       showToast('Approved. AI will execute on its next retry.');
    setBusyId(null);
  };
  const reject = async (run) => {
    const reason = window.prompt('Reason for rejection (optional):', '') || '';
    setBusyId(run.id);
    const { error } = await supabase.from('agent_tool_runs')
      .update({ status: 'rejected', rejected_at: new Date().toISOString(), rejection_reason: reason || null })
      .eq('id', run.id).eq('status', 'pending_approval');
    if (error) showToast(`Reject failed: ${error.message}`);
    else       showToast('Rejected.');
    setBusyId(null);
  };

  const pending  = runs.filter(r => r.status === 'pending_approval');
  const finished = runs.filter(r => r.status !== 'pending_approval');

  if (loading) return <div className="bg-white rounded-xl border border-slate-200 p-5 text-sm text-slate-500">Loading agent activity…</div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Bot size={16} className="text-violet-600"/>
          <h3 className="font-bold text-slate-900">AI Agent · Pending approvals</h3>
          {pending.length > 0 && <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-100 text-violet-800 border border-violet-200 rounded px-1.5 py-0.5">{pending.length} waiting</span>}
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-700"><RefreshCcw size={14}/></button>
      </div>
      <p className="text-xs text-slate-500">When the AI Setup Agent wants to do something irreversible (submit A2P, buy a number), it pauses here for your sign-off. Approve to let it proceed; reject to stop it.</p>

      {pending.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-4 border-2 border-dashed border-slate-200 rounded-lg">
          <ShieldCheck size={20} className="mx-auto text-emerald-500 mb-1"/>
          Nothing waiting. The AI agent isn't blocked on anything from you.
        </div>
      ) : (
        <div className="space-y-2">
          {pending.map(run => {
            const exp = run.expires_at ? new Date(run.expires_at) : null;
            const expired = exp && exp < new Date();
            return (
              <div key={run.id} className="border-2 border-amber-300 bg-amber-50 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900">{TOOL_LABEL[run.tool] || run.tool}</div>
                    <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                      <Clock size={10}/> {fmtAgo(run.created_at)}{exp && ` · expires ${fmtAgo(run.expires_at).replace(' ago','')} from now`}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => approve(run)} disabled={busyId === run.id || expired}
                      className="text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-3 py-1.5 disabled:opacity-50">Approve</button>
                    <button onClick={() => reject(run)} disabled={busyId === run.id}
                      className="text-xs font-bold uppercase tracking-wider bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 rounded-md px-3 py-1.5 disabled:opacity-50">Reject</button>
                  </div>
                </div>
                {expired && <div className="mt-2 text-xs text-rose-700 inline-flex items-center gap-1"><AlertTriangle size={11}/> Expired — agent will need to re-request.</div>}
                <PreviewBlock preview={run.preview}/>
              </div>
            );
          })}
        </div>
      )}

      {finished.length > 0 && (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer hover:text-slate-900">Recent agent activity ({finished.length}) · <button className="underline" onClick={(e) => { e.preventDefault(); setShowAll(s => !s); }}>{showAll ? 'show less' : 'show more'}</button></summary>
          <div className="mt-2 divide-y divide-slate-100">
            {finished.map(run => (
              <div key={run.id} className="py-1.5 flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 ${badgeTone(run.status)}`}>{run.status}</span>
                <span className="text-slate-700">{TOOL_LABEL[run.tool] || run.tool}</span>
                <span className="text-slate-400 ml-auto">{fmtAgo(run.executed_at || run.rejected_at || run.created_at)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function PreviewBlock({ preview }) {
  if (!preview || Object.keys(preview).length === 0) return null;
  const skip = new Set(['action', 'irreversible']);
  const rows = Object.entries(preview).filter(([k, v]) => !skip.has(k) && v != null && v !== '');
  if (rows.length === 0) return null;
  return (
    <div className="mt-2 bg-white border border-slate-200 rounded-md p-2 text-[11px] text-slate-700 font-mono space-y-0.5">
      {preview.irreversible && <div className="text-rose-700 font-bold mb-1">⚠ IRREVERSIBLE ACTION</div>}
      {rows.map(([k, v]) => (
        <div key={k}><span className="text-slate-500">{k}:</span> {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
      ))}
    </div>
  );
}

function badgeTone(status) {
  if (status === 'executed') return 'bg-emerald-50 text-emerald-800 border border-emerald-200';
  if (status === 'approved') return 'bg-sky-50 text-sky-800 border border-sky-200';
  if (status === 'rejected') return 'bg-rose-50 text-rose-800 border border-rose-200';
  if (status === 'expired')  return 'bg-amber-50 text-amber-800 border border-amber-200';
  if (status === 'failed')   return 'bg-rose-50 text-rose-800 border border-rose-200';
  return 'bg-slate-50 text-slate-700 border border-slate-200';
}
